import { ApiError } from '../types/api';

/** Pauses before retry 1..5 after a failed run call. */
export const RUN_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;
/** The server holds a job's lease for 60 s (DEFAULT_LEASE_MS in packages/core); wait that long when it says "busy". */
export const RUN_LEASE_WAIT_MS = 60_000;
/** How many "busy" answers in a row we sit through before giving up (another tab or call may really be running the job). */
export const RUN_MAX_BUSY_WAITS = 5;

export type RunErrorKind =
  /** Network error, timeout, 5xx, 429: try again after a backoff. */
  | 'transient'
  /** 409 import_job_busy: the lease is held; wait for it to expire. Not a failure. */
  | 'busy'
  /** Wrong job state, not found, forbidden, expired session …: retrying cannot help. */
  | 'fatal';

export function classifyRunError(error: unknown): RunErrorKind {
  if (!(error instanceof ApiError)) return 'transient'; // fetch() rejected: connection lost, aborted, DNS …
  if (error.status === 409 && error.body.error === 'import_job_busy') return 'busy';
  if (error.status >= 500 || error.status === 408 || error.status === 429) return 'transient';
  return 'fatal';
}

/** Resolves true after `ms`, calling `onTick(secondsLeft)` every second. Resolves false early when `signal` aborts. */
export function sleepWithCountdown(
  ms: number,
  signal: AbortSignal,
  onTick: (secondsLeft: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve(false);
    const end = Date.now() + ms;
    const tick = () => onTick(Math.max(1, Math.ceil((end - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    const timer = setTimeout(() => finish(true), ms);
    const onAbort = () => finish(false);
    function finish(completed: boolean) {
      clearInterval(interval);
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve(completed);
    }
    signal.addEventListener('abort', onAbort);
  });
}
